import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { OfficeLocation } from "@/api/locations";
import { Button, Divider, useDisclosure } from "@heroui/react";
import { api } from "@/api";
import { toast } from "react-toastify";
import {
  FaCity,
  FaMapPin,
  FaBuilding,
  FaTrainSubway,
  FaPencil,
  FaPlus,
  FaElevator,
  FaRubleSign,
  FaRuler,
  FaTag,
} from "react-icons/fa6";
import { EditLocationModal } from "@/app/components/modals/location/editLocationModal";
import { Office } from "@/api/offices";
import useErrorHandling from "@/hooks/useErrorHandling";
import {
  formatCurrency,
  getCurrentMonth,
  getMonthInRussian,
  sortAlphabeticallyByKey,
} from "@/utils";

export function LocationPage() {
  const { id } = useParams() as { id: string };
  const [location, setLocation] = useState<OfficeLocation | null>(null);
  const [officies, setOfficies] = useState<Office[] | null>(null);
  const [totalPrice, setTotalPrice] = useState<number>(0);

  const handleError = useErrorHandling();
  const navigate = useNavigate();

  const editLocationModalDisclosure = useDisclosure();

  useEffect(() => {
    api.locations
      .get(Number(id))
      .then((response) => {
        setLocation(response.data);
      })
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });

    api.locations
      .getOfficesByLocation(Number(id))
      .then((response) => {
        const sortedData = sortAlphabeticallyByKey(
          response.data,
          "display_name",
        );
        setOfficies(sortedData);
      })
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });

    api.analytics
      .summary(
        getMonthInRussian(getCurrentMonth()),
        new Date().getFullYear().toString(),
        Number(id),
      )
      .then((rls) => {
        setTotalPrice(
          rls.data.rent_plan.reduce(
            (total, item) => total + (item.price ?? 0),
            0,
          ),
        );
      })
      .catch((err) => {
        console.log(err);
      });
  }, [
    id,
    setLocation,
    editLocationModalDisclosure.isOpen,
    handleError,
    navigate,
  ]);

  return (
    <div>
      <EditLocationModal
        disclosure={editLocationModalDisclosure}
        location={location}
      />
      {location && (
        <div className="flex flex-col gap-2 p-2 md:p-4">
          <div className="flex flex-col gap-2 mb-2">
            <span className="text-2xl font-bold">{location.display_name}</span>
            <div className="flex flex-row gap-2 items-center">
              <FaCity />
              <span className="text-sm">
                Город: {location.city_display_name}
              </span>
            </div>
            <div className="flex flex-row gap-2 items-center">
              <FaMapPin />
              <span className="text-sm">Адрес: {location.address}</span>
            </div>
            {location.district && (
              <div className="flex flex-row gap-2 items-center">
                <FaBuilding />
                <span className="text-sm">Район: {location.district}</span>
              </div>
            )}
            {location.metro_station && (
              <div className="flex flex-row gap-2 items-center">
                <FaTrainSubway />
                <span className="text-sm">
                  Станция метро: {location.metro_station}
                </span>
              </div>
            )}
            <div className="flex flex-row gap-2 items-center">
              <FaRuler />
              <span className="text-sm">
                Общая площадь: {location.total_area} м2
              </span>
            </div>
            <div className="flex flex-row gap-2 items-center">
              <FaTag />
              <span className="text-sm">
                Потенциальная выручка: {formatCurrency(totalPrice)} р.
              </span>
            </div>
            <div className="flex flex-row gap-2 items-center">
              <FaTag />
              <span className="text-sm">
                Потенциальная цена за м2:{" "}
                {formatCurrency(totalPrice / location.total_area)} р.
              </span>
            </div>
            <div className="flex flex-col gap-2 mt-2">
              <Button
                startContent={<FaPencil />}
                color="primary"
                variant="solid"
                className="max-w-fit"
                onClick={() => editLocationModalDisclosure.onOpen()}
              >
                Редактировать
              </Button>
            </div>
          </div>
          <Divider className="mt-2 mb-2" />
          <div className="flex flex-col gap-2 mb-2">
            <div className="flex flex-row justify-between w-full items-center mb-2">
              <span className="text-xl font-bold">Офисы</span>
              <div className="flex flex-col gap-2 mt-2">
                <Button
                  as={Link}
                  to={`/dashboard/offices/create?location_id=${id}`}
                  startContent={<FaPlus />}
                  color="primary"
                  variant="solid"
                  className="max-w-fit"
                >
                  Создать
                </Button>
              </div>
            </div>
            {officies && (
              <div className="flex flex-row flex-wrap gap-3 items-center mt-2">
                {officies.map((office, index) => (
                  <Link
                    to={`/dashboard/location/${id}/office/${office.id}`}
                    className="flex flex-col bg-background border-2 border-default-200 rounded-xl cursor-pointer select-none"
                    key={index}
                  >
                    <div className="flex flex-col gap-3 p-4">
                      <span className="text-medium font-bold">
                        {office.display_name}
                      </span>
                      <div className="flex flex-row gap-2 items-center">
                        <FaElevator />
                        <span className="text-sm">{office.floor} этаж</span>
                      </div>
                      <div className="flex flex-row gap-2 items-center">
                        <FaRubleSign />
                        <span className="text-sm">{office.price} руб.</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
